import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";

const API = "/api";

/**
 * iter72 CORTE 1.1 — "🔄 Rotar mi QR" con doble confirmación.
 * Genera token nuevo e invalida el anterior de inmediato.
 */
export const RotateQRConfig = ({ vehicleId, onRotated }) => {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0); // 0 cerrado · 1 advertencia · 2 confirmación final
  const [rotating, setRotating] = useState(false);

  const loadStatus = useCallback(() => {
    axios.get(`${API}/vehicles/${vehicleId}/rotation-status`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ rotations_used: 0, rotations_limit: 3, can_rotate: true }));
  }, [vehicleId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const rotate = async () => {
    setRotating(true);
    try {
      const r = await axios.post(`${API}/vehicles/${vehicleId}/rotate-qr`, { confirm: true });
      setStep(0);
      loadStatus();
      toast.success(t("app.protection.rotate_sticker_done"));
      onRotated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("owner.rotate.error"));
    } finally {
      setRotating(false);
    }
  };

  const remaining = status ? Math.max(0, status.rotations_limit - status.rotations_used) : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rotate-qr-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-bold text-[#0B2545] flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-sky-400" />
            {t("owner.rotate.title")}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {t("owner.rotate.desc")}
            {remaining !== null && (
              <span className="block mt-1 text-slate-500" data-testid="rotate-qr-remaining">
                {t("owner.rotate.remaining", { n: remaining, limit: status.rotations_limit })}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => setStep(1)}
          disabled={status && !status.can_rotate}
          className="shrink-0 px-4 py-2 rounded-xl text-xs font-bold bg-sky-500/20 hover:bg-sky-500/30 text-sky-700 border border-sky-400/40 transition-colors disabled:opacity-40"
          data-testid="rotate-qr-btn"
        >
          🔄 {t("owner.rotate.btn")}
        </button>
      </div>

      {step > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="rotate-qr-modal">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-slate-900 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
              <h4 className="text-[#0B2545] font-bold text-base">
                {step === 1 ? t("owner.rotate.confirm1_title") : t("owner.rotate.confirm2_title")}
              </h4>
            </div>
            {step === 1 ? (
              <p className="text-sm text-slate-600">
                {t("owner.rotate.confirm1_text")}
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                {t("owner.rotate.confirm2_text")}
              </p>
            )}
            <p className="rounded-xl bg-amber-500/15 px-3 py-2 text-xs font-semibold text-amber-200"
               data-testid="rotate-qr-sticker-warning">
              {t("app.protection.rotate_sticker_warning")}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setStep(0)}
                disabled={rotating}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-white/20 text-slate-700 transition-colors"
                data-testid="rotate-qr-cancel"
              >
                {t("owner.vd.cancel")}
              </button>
              {step === 1 ? (
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-amber-500 hover:bg-amber-400 text-black transition-colors"
                  data-testid="rotate-qr-confirm-1"
                >
                  {t("owner.rotate.understood")}
                </button>
              ) : (
                <button
                  onClick={rotate}
                  disabled={rotating}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-red-500 hover:bg-red-400 text-white transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="rotate-qr-confirm-final"
                >
                  {rotating && <Loader2 className="w-3 h-3 animate-spin" />}
                  {t("owner.rotate.yes_rotate")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RotateQRConfig;
